import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, TextField,
  MenuItem, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, Switch, FormControlLabel
} from '@mui/material';
import {
  ArrowBack as BackIcon, Sms as SmsIcon, NoteAdd as NoteIcon, Update as UpdateIcon,
  RequestQuote as QuoteIcon, Build as PartIcon, CloudUpload as UploadIcon,
  SwapHoriz as ShiftIcon, Add as AddIcon,
  Visibility as ViewIcon, PictureAsPdf as PdfIcon, Print as PrintIcon, Edit as EditIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getServiceRequest, updateRepairStatus, addRepairNote, sendRepairSms,
  createPartRequest, getPartRequests, approvePartRequest, rejectPartRequest,
  createRepairQuotation, updateRepairQuotation, getRepairQuotations, getRepairQuotation, addRepairAttachment, shiftRepairBranch, getBranches,
  generateRepairInvoice, getRepairInvoice, updateRepairInvoice, updateRepairType, getOutsourceTechnicians,
  deleteRepair, getDiagnosisParts
} from '../../services/api';
import { useBranchFilter } from '../../context/BranchContext';
import { getErrorMessage } from '../../utils/errorMessage';
import { printRepairQuotation } from '../../utils/repairQuotationPrint';
import { printRepairGrn } from '../../utils/repairGrnPrint';
import { printRepairInvoice } from '../../utils/repairInvoicePrint';
import { canViewOutsourcePrice, isTechnician, getUserRole, canDelete, canShiftBranches } from '../../utils/auth';
import PartPhotoImage from '../../components/PartPhotoImage';
import RepairJobDashboard, { currentWorkflowStep } from './RepairJobDashboard';
import RepairStepDetails from './RepairStepDetails';

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });
const quoteDeviceFromRepair = (d) => {
  if (!d) return '';
  const line1 = [d.device_type, d.device_brand, d.device_model].filter(Boolean).join(' · ');
  const line2 = [
    d.device_serial ? `Serial: ${d.device_serial}` : '',
    d.device_imei ? `IMEI: ${d.device_imei}` : ''
  ].filter(Boolean).join(' · ');
  return [line1, line2].filter(Boolean).join('\n');
};
const userRole = () => getUserRole();
const canApproveParts = () => ['admin', 'super_admin', 'branch_manager', 'manager'].includes(userRole());
const showQuoteInvoice = () => !isTechnician();
const showOutsourcePrice = () => canViewOutsourcePrice();

const ServiceRequestView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { branches, selectedBranchId } = useBranchFilter();
  const [data, setData] = useState(null);
  const [partRequests, setPartRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDialog, setNoteDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [partDialog, setPartDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState(null);
  const [branchDialog, setBranchDialog] = useState(false);
  const [note, setNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [shiftBranchId, setShiftBranchId] = useState('');
  const [approveDialog, setApproveDialog] = useState(null);
  const [approvePrice, setApprovePrice] = useState(0);
  const [quoteItems, setQuoteItems] = useState([]);
  const [newQuoteItem, setNewQuoteItem] = useState({
    item_type: 'part',
    description: '',
    quantity: 1,
    unit_price: 0,
    warranty_months: 0
  });
  const [quoteEditMode, setQuoteEditMode] = useState(false);
  const [quoteDiagnosis, setQuoteDiagnosis] = useState('');
  const [quoteDevice, setQuoteDevice] = useState('');
  const [quoteItemSpec, setQuoteItemSpec] = useState('');
  const [partForm, setPartForm] = useState({
    product_name: '',
    product_code: '',
    quantity: 1,
    unit_cost: 0,
    unit_price: 0,
    part_photo: ''
  });
  const [partPhotoName, setPartPhotoName] = useState('');
  const [partSubmitting, setPartSubmitting] = useState(false);
  const [allBranches, setAllBranches] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [quotationData, setQuotationData] = useState(null);
  const [quotationViewOpen, setQuotationViewOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceViewOpen, setInvoiceViewOpen] = useState(false);
  const [invoiceEditMode, setInvoiceEditMode] = useState(false);
  const [invoiceEditItems, setInvoiceEditItems] = useState([]);
  const [invoiceDiscount, setInvoiceDiscount] = useState(0);
  const [invoiceDeposit, setInvoiceDeposit] = useState(0);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [repairType, setRepairType] = useState('in_house');
  const [outsourceTech, setOutsourceTech] = useState('');
  const [outsourceTechList, setOutsourceTechList] = useState([]);
  const [outsourceRepairPrice, setOutsourceRepairPrice] = useState('');
  const [savingRepairType, setSavingRepairType] = useState(false);
  const [expandedStep, setExpandedStep] = useState(1);
  const [warrantyExtrasBillable, setWarrantyExtrasBillable] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  useEffect(() => {
    if (data?.id) {
      setExpandedStep(currentWorkflowStep(data.workflow || []));
    }
  }, [data?.id]);

  useEffect(() => {
    if (data?.warranty_extras_billable || data?.bill_extras) {
      setWarrantyExtrasBillable(true);
    }
  }, [data?.warranty_extras_billable, data?.bill_extras]);

  const loadData = async () => {
    if (!id || id === 'null' || id === 'undefined' || id === 'ssi-technical' || Number.isNaN(Number(id))) {
      setLoading(false);
      setData(null);
      setError(
        id === 'ssi-technical'
          ? 'SSI Technical is a separate page. Open Repair Management → SSI Technical.'
          : 'Invalid service request ID'
      );
      return;
    }
    try {
      setLoading(true);
      const [res, partsRes, techRes] = await Promise.all([
        getServiceRequest(id),
        getPartRequests(`?repair_id=${id}`),
        getOutsourceTechnicians().catch(() => ({ data: [] }))
      ]);
      setData(res.data);
      setPartRequests(partsRes.data || []);
      setOutsourceTechList(techRes.data || []);
      setRepairType(res.data.repair_location_type || 'in_house');
      setOutsourceTech(res.data.outsource_technician_name || '');
      setOutsourceRepairPrice(
        res.data.outsource_repair_charges != null && res.data.outsource_repair_charges !== ''
          ? String(res.data.outsource_repair_charges)
          : ''
      );
      if (userRole() === 'admin' || userRole() === 'super_admin') {
        const bRes = await getBranches();
        setAllBranches(bRes.data || []);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load service request'));
    } finally {
      setLoading(false);
    }
  };

  const handleSms = async (type) => {
    setSmsSending(true);
    try {
      await sendRepairSms(id, { message_type: type });
      setSuccess('SMS sent');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to send SMS'));
    } finally {
      setSmsSending(false);
    }
  };

  const handleAddNote = async () => {
    try {
      await addRepairNote(id, note);
      setNoteDialog(false);
      setNote('');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add note'));
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await updateRepairStatus(id, newStatus, note);
      setStatusDialog(false);
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update status'));
    }
  };

  const handleUploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = async () => {
        await addRepairAttachment(id, {
          file_name: file.name,
          file_type: file.type.startsWith('image/') ? 'image' : 'file',
          file_url: reader.result
        });
        loadData();
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handlePartRequest = async () => {
    try {
      if (!partForm.product_name?.trim()) {
        setError('Part name is required');
        return;
      }
      const role = (localStorage.getItem('erp_user_role') || '').toLowerCase().replace(/[\s-]+/g, '_');
      if (role === 'technician' && !partForm.part_photo) {
        setError('Part photo is required');
        return;
      }
      setPartSubmitting(true);
      await createPartRequest({
        ...partForm,
        repair_id: parseInt(id, 10),
        part_photo: partForm.part_photo || undefined
      });
      setPartDialog(false);
      setPartForm({ product_name: '', product_code: '', quantity: 1, unit_cost: 0, unit_price: 0, part_photo: '' });
      setPartPhotoName('');
      setSuccess('Part request submitted — visible on Part Requests page');
      window.dispatchEvent(new Event('part-request-created'));
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to submit part request'));
    } finally {
      setPartSubmitting(false);
    }
  };

  const handlePartPhotoPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPartForm((prev) => ({ ...prev, part_photo: reader.result }));
      setPartPhotoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleApprovePart = async () => {
    try {
      await approvePartRequest(approveDialog.id, { approved_unit_price: approvePrice });
      setApproveDialog(null);
      setSuccess('Part request approved');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to approve part'));
    }
  };

  const handleRejectPart = async (partId) => {
    try {
      await rejectPartRequest(partId, { admin_notes: 'Rejected' });
      setSuccess('Part request rejected');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reject part'));
    }
  };

  const handleCreateQuotation = async () => {
    const cleaned = quoteItems
      .filter((i) => (i.description || '').trim())
      .map((i) => ({
        ...i,
        quantity: parseFloat(i.quantity) || 1,
        unit_price: parseFloat(i.unit_price) || 0,
        line_total: (parseFloat(i.quantity) || 1) * (parseFloat(i.unit_price) || 0)
      }));
    if (cleaned.length === 0) return;
    try {
      if (quoteEditMode) {
        await updateRepairQuotation(id, {
          items: cleaned,
          notes: 'Repair quotation updated',
          diagnosis: quoteDiagnosis,
          device: quoteDevice,
          item_specification: quoteItemSpec,
          status: 'draft'
        });
        setSuccess('Quotation updated — ready to resend');
      } else {
        await createRepairQuotation(id, {
          items: cleaned,
          notes: 'Repair quotation from service view',
          diagnosis: quoteDiagnosis,
          device: quoteDevice,
          item_specification: quoteItemSpec
        });
        setSuccess('Quotation created successfully');
      }
      setQuoteDialog(false);
      setQuoteEditMode(false);
      setQuoteItems([]);
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, quoteEditMode ? 'Failed to update quotation' : 'Failed to create quotation'));
    }
  };

  const openCreateQuotationDialog = async () => {
    const labor = parseFloat(data?.diagnosis_labor_charge);
    const osPrice = parseFloat(data?.outsource_repair_charges);
    const seed = [];
    seed.push({
      item_type: 'labor',
      description: 'Labor charge',
      quantity: 1,
      unit_price: !Number.isNaN(labor) && labor > 0 ? labor : 0,
      warranty_months: 0,
      line_total: !Number.isNaN(labor) && labor > 0 ? labor : 0
    });
    if (
      (data?.repair_location_type === 'outsource' || data?.repair_location_type === 'both') &&
      !Number.isNaN(osPrice) &&
      osPrice > 0
    ) {
      seed.push({
        item_type: 'service',
        description: 'Outsource repair charges',
        quantity: 1,
        unit_price: osPrice,
        warranty_months: 0,
        line_total: osPrice
      });
    }

    const parsePayments = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return [];
        }
      }
      return [];
    };

    try {
      const partsRes = await getDiagnosisParts(id);
      const diagParts = partsRes.data || partsRes.diagnosis_parts || [];
      (Array.isArray(diagParts) ? diagParts : []).forEach((p) => {
        const n = parseInt(p.selected_supplier, 10);
        const fromSupplier = [1, 2, 3].includes(n) ? parseFloat(p[`supplier${n}_cost`]) : NaN;
        const cost = !Number.isNaN(fromSupplier) && fromSupplier > 0 ? fromSupplier : 0;
        seed.push({
          item_type: 'part',
          description: p.part_name || 'Part',
          quantity: 1,
          unit_price: cost,
          warranty_months: 0,
          line_total: cost
        });
      });
    } catch (_) {
      (data?.diagnosis_parts || []).forEach((p) => {
        seed.push({
          item_type: 'part',
          description: p.part_name || 'Part',
          quantity: 1,
          unit_price: 0,
          warranty_months: 0,
          line_total: 0
        });
      });
    }

    parsePayments(data?.other_payments).forEach((p) => {
      const reason = (p.reason || p.description || '').trim();
      const price = parseFloat(p.price != null ? p.price : p.unit_price) || 0;
      if (!reason) return;
      seed.push({
        item_type: 'other',
        description: reason,
        quantity: 1,
        unit_price: price,
        warranty_months: 0,
        line_total: price
      });
    });

    try {
      const pr = await getPartRequests(`?repair_id=${id}`);
      (pr.data || []).forEach((r) => {
        if (r.status !== 'approved' && r.status !== 'issued' && r.status !== 'price_approved') return;
        const price = parseFloat(r.approved_unit_price != null ? r.approved_unit_price : r.unit_price);
        const qty = parseFloat(r.quantity) || 1;
        if (Number.isNaN(price)) return;
        if (seed.some((s) => s.item_type === 'part' && s.description === r.product_name && s.unit_price === price)) {
          return;
        }
        seed.push({
          item_type: 'part',
          description: r.product_name || 'Part',
          quantity: qty,
          unit_price: price,
          warranty_months: 0,
          line_total: qty * price
        });
      });
    } catch (_) { /* ignore */ }

    setQuoteItems(seed);
    setQuoteDiagnosis(data?.diagnosis || '');
    setQuoteDevice(quoteDeviceFromRepair(data));
    setQuoteItemSpec(data?.diagnosis_item_specification || '');
    setQuoteEditMode(false);
    setNewQuoteItem({
      item_type: 'part',
      description: '',
      quantity: 1,
      unit_price: 0,
      warranty_months: 0
    });
    setQuoteDialog(true);
  };

  const openEditQuotationDialog = async () => {
    try {
      const qData = await fetchQuotation();
      const items = (qData.quotation?.items || []).map((it) => ({
        item_type: it.item_type || 'part',
        description: it.product_name || it.description || '',
        quantity: parseFloat(it.quantity) || 1,
        unit_price: parseFloat(it.unit_price) || 0,
        warranty_months: parseInt(it.warranty_months, 10) || 0,
        line_total: parseFloat(it.line_total) || ((parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0))
      }));
      setQuoteItems(items);
      setQuoteEditMode(true);
      setQuoteDiagnosis(qData.quotation?.diagnosis || data?.diagnosis || '');
      setQuoteDevice(qData.quotation?.device || quoteDeviceFromRepair(qData.repair || data));
      setQuoteItemSpec(
        qData.quotation?.item_specification
        || (qData.repair || data)?.diagnosis_item_specification
        || ''
      );
      setQuotationData(qData);
      setQuoteDialog(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load quotation for edit'));
    }
  };

  const handleShiftBranch = async () => {
    try {
      await shiftRepairBranch(id, { branch_id: parseInt(shiftBranchId, 10) });
      setBranchDialog(false);
      setSuccess('Job shifted to new branch');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to shift branch'));
    }
  };

  const fetchQuotation = async () => {
    try {
      const res = await getRepairQuotations(id);
      const payload = res.data || {};
      const list = payload.quotations || [];
      const activeId = payload.active_quotation_id;
      const quotation =
        list.find((q) => String(q.id) === String(activeId)) ||
        list[0] ||
        null;
      if (!quotation) {
        throw new Error('No quotation found for this repair');
      }
      return { quotation, repair: payload.repair || data, quotations: list };
    } catch (listErr) {
      // Fallback for older backends that only expose singular /quotation
      const res = await getRepairQuotation(id);
      return res.data;
    }
  };

  const handleViewQuotation = async () => {
    try {
      const qData = await fetchQuotation();
      setQuotationData(qData);
      setQuotationViewOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'No quotation found for this job'));
    }
  };

  const handleDownloadQuotationPdf = async () => {
    try {
      const qData = quotationData || await fetchQuotation();
      printRepairQuotation(qData.quotation, qData.repair || data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to open quotation PDF'));
    }
  };

  const handleDeleteServiceRequest = async () => {
    if (!canDelete() || !id) return;
    const label = data?.grn_number || data?.service_number || data?.repair_number || `#${id}`;
    if (!window.confirm(`Delete service request / JOB ${label}?\n\nThis cannot be undone.`)) return;
    try {
      setLoading(true);
      await deleteRepair(id);
      setSuccess('Service request deleted');
      navigate('/repairs');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete service request'));
      setLoading(false);
    }
  };

  const handleSaveRepairType = async () => {
    if ((repairType === 'outsource' || repairType === 'both') && !outsourceTech.trim()) {
      setError('Please select the outsource technician');
      return;
    }
    try {
      setSavingRepairType(true);
      const payload = {
        repair_location_type: repairType,
        outsource_technician_name:
          repairType === 'outsource' || repairType === 'both' ? outsourceTech.trim() : null
      };
      if ((repairType === 'outsource' || repairType === 'both') && showOutsourcePrice()) {
        payload.outsource_repair_charges = parseFloat(outsourceRepairPrice) || 0;
      }
      await updateRepairType(id, payload);
      setSuccess(
        repairType === 'outsource'
          ? 'Marked as outsource repair'
          : repairType === 'both'
            ? 'Marked as both (in-house + outsource)'
            : 'Marked as in-house repair'
      );
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update repair type'));
    } finally {
      setSavingRepairType(false);
    }
  };

  const seedInvoiceEdit = (payload) => {
    const inv = payload?.invoice || {};
    const items = (inv.items || []).map((it) => ({
      item_type: it.item_type || 'part',
      product_name: it.product_name || it.description || '',
      quantity: parseFloat(it.quantity) || 1,
      unit_price: parseFloat(it.unit_price) || 0,
      line_total: parseFloat(it.line_total) || ((parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)),
      serial_number: it.serial_number || '',
      warranty_months: parseInt(it.warranty_months, 10) || 0
    }));
    setInvoiceEditItems(items.length ? items : [{ item_type: 'part', product_name: '', quantity: 1, unit_price: 0, line_total: 0, serial_number: '', warranty_months: 0 }]);
    setInvoiceDiscount(parseFloat(inv.discount) || 0);
    setInvoiceDeposit(parseFloat(inv.deposit_paid) || 0);
    setInvoiceEditMode(false);
  };

  const handleGenerateInvoice = async () => {
    try {
      setInvoiceGenerating(true);
      setError('');
      const res = await generateRepairInvoice(id);
      const invData = res.data;
      setInvoiceData(invData);
      seedInvoiceEdit(invData);
      setInvoiceViewOpen(true);
      setSuccess(`Invoice ${invData.invoice.invoice_number} generated`);
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to generate invoice — create a quotation first'));
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const handleViewInvoice = async () => {
    try {
      const res = await getRepairInvoice(id);
      setInvoiceData(res.data);
      seedInvoiceEdit(res.data);
      setInvoiceViewOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'No invoice found — generate one first'));
    }
  };

  const handlePrintInvoice = () => {
    if (invoiceData?.invoice) {
      printRepairInvoice(invoiceData.invoice, invoiceData.repair || data);
    }
  };

  const updateEditItem = (index, field, value) => {
    setInvoiceEditItems((prev) => {
      const next = [...prev];
      const row = { ...next[index], [field]: value };
      const qty = parseFloat(row.quantity) || 0;
      const price = parseFloat(row.unit_price) || 0;
      row.line_total = qty * price;
      next[index] = row;
      return next;
    });
  };

  const updateQuoteItem = (index, field, value) => {
    setQuoteItems((prev) => {
      const next = [...prev];
      const row = { ...next[index], [field]: value };
      const qty = parseFloat(row.quantity) || 0;
      const price = parseFloat(row.unit_price) || 0;
      row.line_total = qty * price;
      next[index] = row;
      return next;
    });
  };

  const handleSaveInvoice = async () => {
    const cleaned = invoiceEditItems
      .filter((it) => (it.product_name || '').trim())
      .map((it) => ({
        ...it,
        quantity: parseFloat(it.quantity) || 1,
        unit_price: parseFloat(it.unit_price) || 0,
        line_total: (parseFloat(it.quantity) || 1) * (parseFloat(it.unit_price) || 0)
      }));
    if (!cleaned.length) {
      setError('Add at least one invoice item');
      return;
    }
    try {
      setInvoiceSaving(true);
      setError('');
      const res = await updateRepairInvoice(id, {
        items: cleaned,
        tax_amount: 0,
        discount: invoiceDiscount,
        deposit_paid: invoiceDeposit
      });
      setInvoiceData(res.data);
      seedInvoiceEdit(res.data);
      setSuccess('Invoice updated');
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update invoice'));
    } finally {
      setInvoiceSaving(false);
    }
  };

  const invoiceEditSubtotal = invoiceEditItems.reduce((s, it) => s + ((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)), 0);
  const invoiceEditTotal = invoiceEditSubtotal - (parseFloat(invoiceDiscount) || 0);

  const hasQuotation = Boolean(data?.quotation_id || ['quoted', 'approved'].includes(data?.status));
  const hasInvoice = Boolean(data?.invoice_number || data?.converted_to_invoice);
  const isWarranty = Boolean(
    data && (
      data.service_coverage === 'warranty' ||
      data.is_warranty === true ||
      data.is_warranty === 1 ||
      String(data.warranty_status || '').toLowerCase() === 'warranty'
    )
  );

  if (loading) return <CircularProgress />;
  if (!data) return <Alert severity="error">{error || 'Service request not found'}</Alert>;

  const sn = data.service_number || data.repair_number;
  const images = (data.attachments || []).filter((a) => a.file_type === 'image' || (a.file_url || '').startsWith('data:image'));

  const techLabel = data.repair_location_type === 'outsource' || data.repair_location_type === 'both'
    ? (data.outsource_technician_name || 'Outsource')
    : (data.technician_name || 'Unassigned');
  const typeLabel = data.repair_location_type === 'outsource'
    ? 'Outsource'
    : data.repair_location_type === 'both'
      ? 'Both'
      : 'In-house';

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {data.grn_number || `Job #${sn}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
            <Chip size="small" color="primary" label={(data.status || '').replace(/_/g, ' ') || 'open'} />
            {data.priority && (
              <Chip
                size="small"
                color={data.priority === 'high' || data.priority === 'urgent' ? 'error' : 'warning'}
                label={data.priority}
              />
            )}
            <Chip
              size="small"
              color={data.repair_location_type === 'outsource' ? 'secondary' : data.repair_location_type === 'both' ? 'warning' : 'success'}
              label={typeLabel}
            />
            <Chip size="small" variant="outlined" label={techLabel} />
            {data.device_type && <Chip size="small" variant="outlined" label={data.device_type} />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<PrintIcon />} variant="contained" color="primary" onClick={() => printRepairGrn(data, data.branch)}>
            Print JOB Receipt
          </Button>
          {data.customer_id && (
            <Button variant="outlined" onClick={() => navigate(`/repairs/customers/${data.customer_id}`)}>
              Customer profile
            </Button>
          )}
          {canShiftBranches() && (
            <Button startIcon={<ShiftIcon />} variant="outlined" onClick={() => { setShiftBranchId(data.branch_id || selectedBranchId || ''); setBranchDialog(true); }}>
              Shift Branch
            </Button>
          )}
          {canDelete() && (
            <Button startIcon={<DeleteIcon />} variant="outlined" color="error" onClick={handleDeleteServiceRequest}>
              Delete
            </Button>
          )}
        </Box>
      </Box>

      <RepairJobDashboard
        data={data}
        expandedStep={expandedStep}
        onSelectStep={setExpandedStep}
      >
        <RepairStepDetails
          data={data}
          expandedStep={expandedStep}
          setExpandedStep={setExpandedStep}
          images={images}
          partRequests={partRequests}
          outsourceTechList={outsourceTechList}
          onReload={loadData}
          setError={setError}
          setSuccess={setSuccess}
          showQuoteInvoice={showQuoteInvoice()}
          onUploadImages={handleUploadImages}
          onImageClick={setImageDialog}
          onRequestPart={() => setPartDialog(true)}
          onApprovePart={(p) => { setApproveDialog(p); setApprovePrice(p.unit_price); }}
          onRejectPart={handleRejectPart}
          canApproveParts={canApproveParts()}
          fmt={fmt}
          onSms={handleSms}
          smsSending={smsSending}
          hasQuotation={hasQuotation}
          hasInvoice={hasInvoice}
          isWarranty={isWarranty}
          warrantyExtrasBillable={warrantyExtrasBillable}
          onViewInvoice={handleViewInvoice}
          onGenerateInvoice={handleGenerateInvoice}
          invoiceGenerating={invoiceGenerating}
          onCreateQuotation={openCreateQuotationDialog}
          onEditQuotation={openEditQuotationDialog}
          onViewQuotation={handleViewQuotation}
        />
      </RepairJobDashboard>

      <Grid container spacing={2} sx={{ my: 2 }}>
        <Grid item xs={6}><Card><CardContent><Typography color="text.secondary">Parts Used</Typography><Typography variant="h5">{fmt(data.parts_cost)}</Typography></CardContent></Card></Grid>
        <Grid item xs={6}><Card><CardContent><Typography color="text.secondary">Total Cost</Typography><Typography variant="h5">{isWarranty && !warrantyExtrasBillable ? 'FREE (Warranty)' : fmt(data.total_amount)}</Typography></CardContent></Card></Grid>
      </Grid>

      {isWarranty && (
        <Alert severity="success" sx={{ mb: 2 }}>
          FREE — Warranty coverage (core repair/exchange is not billed)
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={warrantyExtrasBillable}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    setWarrantyExtrasBillable(checked);
                    if (!id) return;
                    try {
                      await updateRepairStatus(id, { warranty_extras_billable: checked });
                      setSuccess(
                        checked
                          ? 'Paid extras enabled — quotation/invoice available'
                          : 'Free warranty — complete repair in Step 4 (no quotation required)'
                      );
                      loadData();
                    } catch (err) {
                      setWarrantyExtrasBillable(!checked);
                      setError(getErrorMessage(err, 'Failed to save warranty option'));
                    }
                  }}
                  color="warning"
                />
              }
              label="Customer wants paid extras / billable items"
            />
          </Box>
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" startIcon={<UpdateIcon />} onClick={() => { setNewStatus(data.status); setStatusDialog(true); }}>Update Status</Button>
        <Button variant="outlined" startIcon={<NoteIcon />} onClick={() => setNoteDialog(true)}>Add Note</Button>
        {showQuoteInvoice() && (
          <>
            {(!isWarranty || warrantyExtrasBillable) && (
              <Button variant="outlined" color="secondary" startIcon={<QuoteIcon />} onClick={openCreateQuotationDialog}>Create Quotation</Button>
            )}
            {hasQuotation && (
              <>
                <Button variant="contained" color="secondary" startIcon={<ViewIcon />} onClick={handleViewQuotation}>View Quotation</Button>
                <Button variant="outlined" color="secondary" startIcon={<EditIcon />} onClick={openEditQuotationDialog}>Edit Quotation</Button>
                <Button variant="outlined" color="secondary" startIcon={<PdfIcon />} onClick={handleDownloadQuotationPdf}>Download PDF</Button>
              </>
            )}
          </>
        )}
        <Button variant="outlined" startIcon={<PartIcon />} onClick={() => setPartDialog(true)}>Request Part</Button>
        <Button variant="outlined" startIcon={<SmsIcon />} disabled={smsSending} onClick={() => handleSms('progress')}>SMS Progress</Button>
        {showQuoteInvoice() && (
          <>
            <Button variant="outlined" startIcon={<SmsIcon />} disabled={smsSending} onClick={() => handleSms('quotation')}>SMS Quotation Link</Button>
            <Button variant="outlined" startIcon={<SmsIcon />} disabled={smsSending} onClick={() => handleSms('invoice')}>SMS Invoice Link</Button>
            {hasInvoice ? (
              <>
                <Button variant="contained" color="success" startIcon={<ViewIcon />} onClick={handleViewInvoice}>
                  View Invoice ({data.invoice_number})
                </Button>
                <Button variant="outlined" color="success" startIcon={<PdfIcon />} onClick={async () => {
                  try {
                    const res = invoiceData || (await getRepairInvoice(id)).data;
                    printRepairInvoice(res.invoice, res.repair || data);
                  } catch (err) {
                    setError(getErrorMessage(err, 'Failed to print invoice'));
                  }
                }}>
                  Print Invoice
                </Button>
              </>
            ) : (
              (!isWarranty || warrantyExtrasBillable) && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PdfIcon />}
                  disabled={invoiceGenerating}
                  onClick={handleGenerateInvoice}
                >
                  {invoiceGenerating ? 'Generating...' : 'Generate Invoice'}
                </Button>
              )
            )}
          </>
        )}
      </Box>

      <Dialog open={quotationViewOpen} onClose={() => setQuotationViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Quotation {quotationData?.quotation?.quotation_number || ''}
        </DialogTitle>
        <DialogContent>
          {quotationData?.quotation && (
            <>
              <Typography sx={{ mb: 2 }}>
                <strong>Customer:</strong> {quotationData.repair?.customer_name || quotationData.quotation.customer_name}<br />
                <strong>Total:</strong> {fmt(quotationData.quotation.total_amount)}<br />
                <strong>Valid Until:</strong> {quotationData.quotation.valid_until ? new Date(quotationData.quotation.valid_until).toLocaleDateString() : '-'}
              </Typography>
              {(quotationData.quotation.device || quoteDeviceFromRepair(quotationData.repair)) && (
                <Alert severity="info" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                  <strong>Device:</strong><br />
                  {quotationData.quotation.device || quoteDeviceFromRepair(quotationData.repair)}
                </Alert>
              )}
              {(quotationData.quotation.item_specification || quotationData.repair?.diagnosis_item_specification) && (
                <Alert sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                  <strong>Item specification:</strong><br />
                  {quotationData.quotation.item_specification || quotationData.repair?.diagnosis_item_specification}
                </Alert>
              )}
              {(quotationData.quotation.diagnosis || quotationData.repair?.diagnosis) && (
                <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  <strong>Diagnosis:</strong><br />
                  {quotationData.quotation.diagnosis || quotationData.repair?.diagnosis}
                </Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Warranty</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(quotationData.quotation.items || []).map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.item_type}</TableCell>
                      <TableCell>{item.product_name || item.description}</TableCell>
                      <TableCell>{item.warranty_months ? `${item.warranty_months} mo` : '-'}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{fmt(item.unit_price)}</TableCell>
                      <TableCell>{fmt(item.line_total || item.quantity * item.unit_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuotationViewOpen(false)}>Close</Button>
          {hasQuotation && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setQuotationViewOpen(false);
                openEditQuotationDialog();
              }}
            >
              Edit Quotation
            </Button>
          )}
          <Button variant="contained" startIcon={<PdfIcon />} onClick={() => printRepairQuotation(quotationData.quotation, quotationData.repair || data)}>
            Print / Save PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={invoiceViewOpen} onClose={() => { setInvoiceViewOpen(false); setInvoiceEditMode(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Invoice {invoiceData?.invoice?.invoice_number || ''}</DialogTitle>
        <DialogContent>
          {invoiceData?.invoice && (
            <>
              <Typography sx={{ mb: 2 }}>
                <strong>Customer:</strong> {invoiceData.repair?.customer_name || data.customer_name}<br />
                <strong>Date:</strong> {invoiceData.invoice.invoice_date ? new Date(invoiceData.invoice.invoice_date).toLocaleDateString() : '-'}<br />
                <strong>Quotation #:</strong> {invoiceData.invoice.quotation_number || '-'}<br />
                {!invoiceEditMode && (
                  <>
                    <strong>Total:</strong> {fmt(invoiceData.invoice.total_amount)} ·{' '}
                    <strong>Balance Due:</strong> {fmt(invoiceData.invoice.balance_due)}
                  </>
                )}
              </Typography>

              {invoiceEditMode ? (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Serial Number</TableCell>
                        <TableCell>Warranty (months)</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell width={40} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoiceEditItems.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <TextField select size="small" value={item.item_type}
                              onChange={(e) => updateEditItem(i, 'item_type', e.target.value)} sx={{ minWidth: 100 }}>
                              <MenuItem value="part">Part</MenuItem>
                              <MenuItem value="labor">Labor</MenuItem>
                              <MenuItem value="service">Service</MenuItem>
                              <MenuItem value="other">Other</MenuItem>
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField size="small" fullWidth value={item.product_name}
                              onChange={(e) => updateEditItem(i, 'product_name', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={item.serial_number || ''} sx={{ width: 120 }}
                              onChange={(e) => updateEditItem(i, 'serial_number', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" value={item.warranty_months || 0} sx={{ width: 90 }}
                              onChange={(e) => updateEditItem(i, 'warranty_months', parseInt(e.target.value, 10) || 0)} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" value={item.quantity} sx={{ width: 80 }}
                              onChange={(e) => updateEditItem(i, 'quantity', e.target.value)} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" value={item.unit_price} sx={{ width: 100 }}
                              onChange={(e) => updateEditItem(i, 'unit_price', e.target.value)} />
                          </TableCell>
                          <TableCell>{fmt(item.line_total)}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error"
                              onClick={() => setInvoiceEditItems((prev) => prev.filter((_, idx) => idx !== i))}
                              disabled={invoiceEditItems.length <= 1}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
                    onClick={() => setInvoiceEditItems((prev) => [...prev, { item_type: 'part', product_name: '', quantity: 1, unit_price: 0, line_total: 0, serial_number: '', warranty_months: 0 }])}>
                    Add line
                  </Button>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Discount" value={invoiceDiscount}
                        onChange={(e) => setInvoiceDiscount(e.target.value)} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField fullWidth size="small" type="number" label="Deposit paid" value={invoiceDeposit}
                        onChange={(e) => setInvoiceDeposit(e.target.value)} />
                    </Grid>
                  </Grid>
                  <Typography sx={{ mt: 2 }}>
                    <strong>Total: {fmt(invoiceEditTotal)}</strong> · Balance: {fmt(Math.max(0, invoiceEditTotal - (parseFloat(invoiceDeposit) || 0)))}
                  </Typography>
                </>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Serial</TableCell>
                        <TableCell>Warranty</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(invoiceData.invoice.items || []).map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>{item.item_type}</TableCell>
                          <TableCell>{item.product_name || item.description}</TableCell>
                          <TableCell>{item.serial_number || '-'}</TableCell>
                          <TableCell>{item.warranty_months ? `${item.warranty_months} mo` : '-'}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{fmt(item.unit_price)}</TableCell>
                          <TableCell>{fmt(item.line_total || item.quantity * item.unit_price)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Typography sx={{ mt: 2 }}>
                    <strong>Total: {fmt(invoiceData.invoice.total_amount)}</strong>
                    {parseFloat(invoiceData.invoice.discount || 0) > 0 ? ` · Discount: ${fmt(invoiceData.invoice.discount)}` : ''}
                  </Typography>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInvoiceViewOpen(false); setInvoiceEditMode(false); }}>Close</Button>
          {invoiceEditMode ? (
            <>
              <Button onClick={() => { seedInvoiceEdit(invoiceData); }}>Cancel edit</Button>
              <Button variant="contained" color="success" disabled={invoiceSaving} onClick={handleSaveInvoice}>
                {invoiceSaving ? 'Saving...' : 'Save Invoice'}
              </Button>
            </>
          ) : (
            <>
              <Button startIcon={<EditIcon />} onClick={() => setInvoiceEditMode(true)}>Edit Invoice</Button>
              <Button variant="contained" startIcon={<PdfIcon />} onClick={handlePrintInvoice}>
                Print / Save PDF
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog open={!!imageDialog} onClose={() => setImageDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {imageDialog?.label || imageDialog?.file_name || 'Image Preview'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          {imageDialog?.photoUrl ? (
            <PartPhotoImage
              photoUrl={imageDialog.photoUrl}
              thumb={false}
              sx={{ maxWidth: '100%', maxHeight: '70vh', mx: 'auto' }}
            />
          ) : imageDialog?.file_url ? (
            <img src={imageDialog.file_url} alt={imageDialog.file_name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          ) : null}
        </DialogContent>
        <DialogActions><Button onClick={() => setImageDialog(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Part request dialog */}
      <Dialog open={partDialog} onClose={() => setPartDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Part</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth label="Part Name" value={partForm.product_name} onChange={(e) => setPartForm({ ...partForm, product_name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Part Code" value={partForm.product_code} onChange={(e) => setPartForm({ ...partForm, product_code: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Quantity" type="number" value={partForm.quantity} onChange={(e) => setPartForm({ ...partForm, quantity: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Unit Cost" type="number" value={partForm.unit_cost} onChange={(e) => setPartForm({ ...partForm, unit_cost: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Unit Price (charge)" type="number" value={partForm.unit_price} onChange={(e) => setPartForm({ ...partForm, unit_price: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                {isTechnician() ? 'Upload Part Photo *' : 'Upload Part Photo'}
                <input type="file" hidden accept="image/*" onChange={handlePartPhotoPick} />
              </Button>
              {partPhotoName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{partPhotoName}</Typography>
              )}
              {partForm.part_photo && (
                <Box
                  component="img"
                  src={partForm.part_photo}
                  alt="part preview"
                  sx={{ mt: 1, maxWidth: '100%', maxHeight: 180, borderRadius: 1, display: 'block' }}
                />
              )}
              {isTechnician() && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Photo is required for technician part requests
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePartRequest} disabled={partSubmitting}>
            {partSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve part dialog */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Part & Price</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{approveDialog?.product_name}</Typography>
          {approveDialog?.part_photo_url && (
            <PartPhotoImage
              photoUrl={approveDialog.part_photo_url}
              thumb={false}
              sx={{ width: '100%', maxHeight: 200, mb: 2 }}
            />
          )}
          <TextField fullWidth label="Approved Unit Price" type="number" value={approvePrice} onChange={(e) => setApprovePrice(e.target.value)} />
        </DialogContent>
        <DialogActions><Button onClick={() => setApproveDialog(null)}>Cancel</Button><Button variant="contained" color="success" onClick={handleApprovePart}>Approve</Button></DialogActions>
      </Dialog>

      {/* Quotation dialog */}
      <Dialog open={quoteDialog} onClose={() => { setQuoteDialog(false); setQuoteEditMode(false); }} maxWidth="md" fullWidth>
        <DialogTitle>{quoteEditMode ? 'Edit Quotation' : 'Create Quotation'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Device, item specification, and diagnosis are filled from Step 2. Edit them on this quotation if needed.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Device"
            value={quoteDevice}
            onChange={(e) => setQuoteDevice(e.target.value)}
            helperText="Auto-filled from the job device — editable"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Item specification"
            value={quoteItemSpec}
            onChange={(e) => setQuoteItemSpec(e.target.value)}
            helperText="Auto-filled from Step 2 item specification — editable"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Diagnosis"
            value={quoteDiagnosis}
            onChange={(e) => setQuoteDiagnosis(e.target.value)}
            helperText="Auto-filled from Step 2 fault description — editable"
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth select size="small" label="Type" value={newQuoteItem.item_type}
                onChange={(e) => setNewQuoteItem({ ...newQuoteItem, item_type: e.target.value })}>
                <MenuItem value="part">Part</MenuItem>
                <MenuItem value="labor">Labor</MenuItem>
                <MenuItem value="service">Service</MenuItem>
                <MenuItem value="other">Other payment</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth size="small" label="Description / reason" value={newQuoteItem.description}
                onChange={(e) => setNewQuoteItem({ ...newQuoteItem, description: e.target.value })} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Warranty (months)" type="number"
                value={newQuoteItem.warranty_months}
                onChange={(e) => setNewQuoteItem({ ...newQuoteItem, warranty_months: parseInt(e.target.value, 10) || 0 })}
                helperText="0 = no warranty" />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField fullWidth size="small" label="Qty" type="number" value={newQuoteItem.quantity}
                onChange={(e) => setNewQuoteItem({ ...newQuoteItem, quantity: parseFloat(e.target.value) || 1 })} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField fullWidth size="small" label="Price" type="number" value={newQuoteItem.unit_price}
                onChange={(e) => setNewQuoteItem({ ...newQuoteItem, unit_price: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <IconButton color="primary" onClick={() => {
                if (newQuoteItem.description) {
                  setQuoteItems([...quoteItems, {
                    ...newQuoteItem,
                    line_total: newQuoteItem.quantity * newQuoteItem.unit_price
                  }]);
                  setNewQuoteItem({
                    item_type: 'part',
                    description: '',
                    quantity: 1,
                    unit_price: 0,
                    warranty_months: 0
                  });
                }
              }}><AddIcon /></IconButton>
            </Grid>
          </Grid>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Warranty</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Total</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {quoteItems.map((item, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField select size="small" value={item.item_type || 'part'}
                      onChange={(e) => updateQuoteItem(i, 'item_type', e.target.value)} sx={{ minWidth: 110 }}>
                      <MenuItem value="part">Part</MenuItem>
                      <MenuItem value="labor">Labor</MenuItem>
                      <MenuItem value="service">Service</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={item.description}
                      onChange={(e) => updateQuoteItem(i, 'description', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={item.warranty_months || 0} sx={{ width: 80 }}
                      onChange={(e) => updateQuoteItem(i, 'warranty_months', parseInt(e.target.value, 10) || 0)} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={item.quantity} sx={{ width: 70 }}
                      onChange={(e) => updateQuoteItem(i, 'quantity', parseFloat(e.target.value) || 0)} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={item.unit_price} sx={{ width: 100 }}
                      onChange={(e) => updateQuoteItem(i, 'unit_price', parseFloat(e.target.value) || 0)} />
                  </TableCell>
                  <TableCell>{fmt((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => setQuoteItems(quoteItems.filter((_, idx) => idx !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ mt: 2 }}><strong>Total:</strong> {fmt(quoteItems.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0))}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQuoteDialog(false); setQuoteEditMode(false); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={quoteItems.filter((i) => (i.description || '').trim()).length === 0}
            onClick={handleCreateQuotation}
          >
            {quoteEditMode ? 'Save Quotation' : 'Save Quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shift branch dialog */}
      <Dialog open={branchDialog} onClose={() => setBranchDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Shift Job to Branch</DialogTitle>
        <DialogContent>
          <TextField fullWidth select label="Branch" sx={{ mt: 1 }} value={shiftBranchId}
            onChange={(e) => setShiftBranchId(e.target.value)}>
            {(allBranches.length ? allBranches : branches).map((b) => (
              <MenuItem key={b.id} value={b.id}>{b.name} ({b.branch_code})</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions><Button onClick={() => setBranchDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleShiftBranch}>Shift</Button></DialogActions>
      </Dialog>

      <Dialog open={noteDialog} onClose={() => setNoteDialog(false)}>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent><TextField fullWidth multiline rows={3} value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 1 }} /></DialogContent>
        <DialogActions><Button onClick={() => setNoteDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleAddNote}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <TextField fullWidth select label="Status" sx={{ mt: 1, mb: 2, minWidth: 200 }} value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
            {['pending', 'diagnosis', 'quoted', 'approved', 'in_progress', 'completed', 'delivered', 'cancelled'].map((s) =>
              <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
          <TextField fullWidth multiline rows={2} label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        </DialogContent>
        <DialogActions><Button onClick={() => setStatusDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleStatusUpdate}>Update & SMS</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceRequestView;
